import moment from 'moment';

/**
 * Utility functions for SMART Goals review scheduling
 */

/**
 * Check if a SMART goal review is overdue
 * @param {Object} goal - SMART goal object
 * @returns {boolean} - True if review is overdue
 */
export const isReviewOverdue = (goal) => {
  if (!goal?.timeline?.nextReviewDate) {
    return false;
  }
  return moment().isAfter(moment(goal.timeline.nextReviewDate));
};

/**
 * Get days until next review
 * @param {Object} goal - SMART goal object
 * @returns {number|null} - Days until review (negative if overdue)
 */
export const getDaysUntilReview = (goal) => {
  if (!goal?.timeline?.nextReviewDate) {
    return null;
  }
  return moment(goal.timeline.nextReviewDate).diff(moment(), 'days');
};

/**
 * Check if review is due soon (within specified days)
 * @param {Object} goal - SMART goal object
 * @param {number} daysThreshold - Number of days to consider "due soon"
 * @returns {boolean} - True if review is due soon
 */
export const isReviewDueSoon = (goal, daysThreshold = 7) => {
  const daysUntil = getDaysUntilReview(goal);
  return daysUntil !== null && daysUntil <= daysThreshold && daysUntil >= 0;
};

/**
 * Get review status with color and message
 * @param {Object} goal - SMART goal object
 * @returns {Object} - Status object with type, message, and color
 */
export const getReviewStatus = (goal) => {
  if (!goal?.timeline?.nextReviewDate) {
    return {
      type: 'warning',
      message: 'No review scheduled',
      color: '#fa8c16',
      priority: 2
    };
  }

  const daysUntil = getDaysUntilReview(goal);
  
  if (daysUntil < 0) {
    return {
      type: 'error',
      message: `Review overdue by ${Math.abs(daysUntil)} days`,
      color: '#f5222d',
      priority: 1
    };
  }
  
  if (daysUntil <= 7) {
    return {
      type: 'warning',
      message: `Review due in ${daysUntil} days`,
      color: '#fa8c16',
      priority: 2
    };
  }
  
  return {
    type: 'success',
    message: `Review scheduled for ${moment(goal.timeline.nextReviewDate).format('MMM DD')}`,
    color: '#52c41a',
    priority: 3
  };
};

/**
 * Filter goals that need review attention (overdue or due soon)
 * @param {Array} goals - Array of SMART goals
 * @param {number} daysThreshold - Days threshold for "due soon"
 * @returns {Array} - Goals needing review attention
 */
export const getGoalsNeedingReview = (goals, daysThreshold = 7) => {
  return goals.filter(goal => 
    isReviewOverdue(goal) || isReviewDueSoon(goal, daysThreshold)
  );
};

/**
 * Sort goals by review urgency
 * @param {Array} goals - Array of SMART goals
 * @returns {Array} - Goals sorted by review urgency (most urgent first)
 */
export const sortGoalsByReviewUrgency = (goals) => {
  return [...goals].sort((a, b) => {
    const statusA = getReviewStatus(a);
    const statusB = getReviewStatus(b);
    
    // Sort by priority (1 = highest priority)
    if (statusA.priority !== statusB.priority) {
      return statusA.priority - statusB.priority;
    }
    
    // If same priority, sort by days until review
    const daysA = getDaysUntilReview(a) || 999;
    const daysB = getDaysUntilReview(b) || 999;
    
    return daysA - daysB;
  });
};

/**
 * Generate review reminder notifications
 * @param {Array} goals - Array of SMART goals
 * @returns {Array} - Array of notification objects
 */
export const generateReviewReminders = (goals) => {
  const reminders = [];
  
  goals.forEach(goal => {
    const status = getReviewStatus(goal);
    
    if (status.type === 'error') {
      reminders.push({
        id: `review-overdue-${goal._id}`,
        type: 'error',
        title: 'Review Overdue',
        message: `${goal.smartGoal || goal.specific?.objective} - ${status.message}`,
        goalId: goal._id,
        priority: 'high',
        action: 'Complete Review'
      });
    } else if (status.type === 'warning' && isReviewDueSoon(goal, 3)) {
      reminders.push({
        id: `review-due-${goal._id}`,
        type: 'warning',
        title: 'Review Due Soon',
        message: `${goal.smartGoal || goal.specific?.objective} - ${status.message}`,
        goalId: goal._id,
        priority: 'medium',
        action: 'Schedule Review'
      });
    }
  });
  
  return reminders;
};

/**
 * Calculate review frequency suggestions based on goal characteristics
 * @param {Object} goal - SMART goal object
 * @returns {Object} - Suggested review frequency with reasoning
 */
export const suggestReviewFrequency = (goal) => {
  const suggestions = [];
  
  // Base frequency on tier
  if (goal.tier === 1) {
    suggestions.push({ frequency: 'monthly', reason: 'Beginner tier goals benefit from frequent check-ins' });
  } else if (goal.tier === 4) {
    suggestions.push({ frequency: 'bi-weekly', reason: 'Advanced goals require close monitoring' });
  } else {
    suggestions.push({ frequency: 'monthly', reason: 'Standard monitoring frequency' });
  }
  
  // Adjust based on completion rate
  if (goal.completionRate < 25) {
    suggestions.push({ frequency: 'weekly', reason: 'Low progress requires more frequent reviews' });
  } else if (goal.completionRate > 75) {
    suggestions.push({ frequency: 'bi-monthly', reason: 'Good progress allows for less frequent reviews' });
  }
  
  // Adjust based on status
  if (goal.status === 'On Hold') {
    suggestions.push({ frequency: 'monthly', reason: 'On-hold goals need regular reassessment' });
  } else if (goal.status === 'In Progress') {
    suggestions.push({ frequency: 'bi-weekly', reason: 'Active goals benefit from regular monitoring' });
  }
  
  // Return the most frequent suggestion (most conservative)
  const frequencies = { 'weekly': 1, 'bi-weekly': 2, 'monthly': 4, 'bi-monthly': 8 };
  const mostFrequent = suggestions.reduce((prev, curr) => 
    frequencies[curr.frequency] < frequencies[prev.frequency] ? curr : prev
  );
  
  return {
    recommended: mostFrequent.frequency,
    reasoning: suggestions.map(s => s.reason).join('; '),
    alternatives: suggestions.filter(s => s.frequency !== mostFrequent.frequency)
  };
};

/**
 * Format review date for display
 * @param {string|Date} date - Review date
 * @param {boolean} relative - Whether to show relative time
 * @returns {string} - Formatted date string
 */
export const formatReviewDate = (date, relative = false) => {
  if (!date) return 'Not scheduled';
  
  const momentDate = moment(date);
  
  if (relative) {
    const now = moment();
    const diffDays = momentDate.diff(now, 'days');
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  }
  
  return momentDate.format('MMM DD, YYYY');
};

/**
 * Get next suggested review dates
 * @param {string} frequency - Review frequency ('weekly', 'bi-weekly', 'monthly', 'bi-monthly')
 * @param {Date} fromDate - Starting date (defaults to today)
 * @returns {Array} - Array of next 3 suggested dates
 */
export const getNextReviewDates = (frequency = 'monthly', fromDate = new Date()) => {
  const dates = [];
  let current = moment(fromDate);
  
  const intervals = {
    'weekly': 1,
    'bi-weekly': 2,
    'monthly': 4,
    'bi-monthly': 8
  };
  
  const weeks = intervals[frequency] || 4;
  
  for (let i = 0; i < 3; i++) {
    current = current.clone().add(weeks, 'weeks');
    dates.push({
      date: current.toDate(),
      formatted: current.format('MMM DD, YYYY'),
      relative: current.fromNow()
    });
  }
  
  return dates;
};
